/**
 * Auth Page — Login / Sign Up / Forgot Password
 * Light theme consistent with app-wide design
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TERMS_URL, PRIVACY_URL } from '../lib/legalUrls';
import { useAuthStore, useSession } from '../store/useAuthStore';
import { APP_VERSION } from '../config/constants';

type AuthMode = 'login' | 'signup' | 'forgot';

/** Determine the Auth page's initial mode from a location search string (e.g. "?tab=signup"). */
export function initialAuthMode(search: string): 'login' | 'signup' {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'signup' ? 'signup' : 'login';
}

export default function AuthPage() {
  const [mode, setMode] = useState<AuthMode>(() => initialAuthMode(window.location.search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuthStore();
  const session = useSession();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (session) {
    navigate('/app', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(null);
    setSubmitting(true);

    try {
      if (mode === 'forgot') {
        await resetPassword(email);
        setSuccess('Password reset email sent. Check your inbox.');
        return;
      }

      if (mode === 'signup') {
        if (password !== confirmPassword) {
          setLocalError('Passwords do not match.');
          return;
        }
        if (password.length < 6) {
          setLocalError('Password must be at least 6 characters.');
          return;
        }
        await signUp(email, password);
        setSuccess('Check your email to confirm your account.');
        return;
      }

      // Login
      await signIn(email, password);
      navigate('/app', { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Authentication failed.';
      setLocalError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLocalError(null);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : 'Google sign-in failed.');
    }
  };

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setLocalError(null);
    setSuccess(null);
  };

  const inputClass = 'w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00d4b8]/40 focus:border-[#00d4b8] transition-colors text-sm';
  const labelClass = 'block text-sm font-medium text-gray-600 mb-1';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-[#00d4b8]/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Linguist<span className="text-[#007a6d]">Pro</span>
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            AI-powered multilingual transcription
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {mode === 'login' && 'Sign in to your account'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'forgot' && 'Reset your password'}
          </h2>

          {/* Google OAuth */}
          {mode !== 'forgot' && (
            <>
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors mb-4 text-sm font-medium"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Continue with Google
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-gray-400">or</span>
                </div>
              </div>
            </>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className={labelClass}>Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={inputClass}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            {mode !== 'forgot' && (
              <div>
                <label htmlFor="password" className={labelClass}>Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className={inputClass}
                  placeholder="Min. 6 characters"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>Confirm password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className={inputClass}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>
            )}

            {localError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {localError}
              </div>
            )}

            {success && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 px-4 bg-[#00d4b8] text-[#0d1117] font-semibold rounded-lg hover:bg-[#00bfa8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                <>
                  {mode === 'login' && 'Sign in'}
                  {mode === 'signup' && 'Create account'}
                  {mode === 'forgot' && 'Send reset email'}
                </>
              )}
            </button>
          </form>

          {/* POPIA: sign-up consent notice */}
          {mode === 'signup' && (
            <p className="mt-4 text-xs text-center text-gray-400 leading-relaxed">
              By creating an account you agree to our{' '}
              <a href={TERMS_URL} rel="noopener" className="text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={PRIVACY_URL} rel="noopener" className="text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors">
                Privacy Policy
              </a>.
            </p>
          )}

          {/* Mode switches */}
          <div className="mt-6 text-sm text-center text-gray-500 space-y-2">
            {mode === 'login' && (
              <>
                <button onClick={() => switchMode('forgot')} className="text-[#007a6d] hover:text-[#005f56] transition-colors">
                  Forgot your password?
                </button>
                <p>
                  Don't have an account?{' '}
                  <button onClick={() => switchMode('signup')} className="text-[#007a6d] hover:text-[#005f56] font-medium transition-colors">
                    Sign up free
                  </button>
                </p>
              </>
            )}
            {mode === 'signup' && (
              <p>
                Already have an account?{' '}
                <button onClick={() => switchMode('login')} className="text-[#007a6d] hover:text-[#005f56] font-medium transition-colors">
                  Sign in
                </button>
              </p>
            )}
            {mode === 'forgot' && (
              <button onClick={() => switchMode('login')} className="text-[#007a6d] hover:text-[#005f56] transition-colors">
                Back to sign in
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-400">
          LinguistPro {APP_VERSION} &middot; Tech Sight (Pty) Ltd
        </p>
      </div>
    </div>
  );
}
